import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import DraftsList from "./drafts/DraftsList";
import { ArrowIcon, SortIcon, PlusIcon } from "../../../icons";
import { usePlanning } from "../context/PlanningContext";
import { InProgressSection } from "./builds";
import { useBuildJobs } from "../hooks/useBuildJobs";
import { ConfirmationDialog } from "../../../components/common";
// @ts-ignore
import dotsAnimation from "../../../../assets/dots.json";
const StartNewPlan = ({ onStartNewPlan, onResumePlan, onViewBuildDetail }) => {
    const [activeTab, setActiveTab] = useState('drafts');
    const { plans, isLoadingPlans, plansError, loadPlans, deletePlan, isAuthReady } = usePlanning();
    // Build jobs state
    const { inProgressJobs, completedJobs, pauseBuild, resumeBuild, cancelBuild, removeFromQueue, markBuildAsSeen } = useBuildJobs();
    // Confirmation dialog state
    const [confirmDialog, setConfirmDialog] = useState(null);
    // Load plans when auth is ready
    useEffect(() => {
        if (isAuthReady) {
            console.log('[StartNewPlan] Auth ready, loading plans...');
            loadPlans();
        }
        else {
            console.log('[StartNewPlan] Waiting for auth to be ready before loading plans...');
        }
    }, [isAuthReady, loadPlans]);
    // Helper to format date as "29 Sep 2025"
    const formatDate = (dateStr) => {
        if (!dateStr)
            return undefined;
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };
    // Convert PlanListItem to PlanItem format for DraftsList (DRAFTS only)
    // Exclude plans that are currently building (they appear in buildJobs section instead)
    const draftPlans = plans
        .filter(plan => plan.status !== 'building' && plan.status !== 'completed') // Only drafts
        .map(plan => ({
        id: plan.id,
        title: plan.title,
        slideCount: plan.slideCount,
        fileName: plan.fileName,
        status: 'draft',
    }));
    // Convert completedJobs from useBuildJobs to PlanItem format
    const completedBuildsList = completedJobs.map(job => ({
        id: job.planId,
        title: job.planTitle,
        slideCount: job.slideCount,
        fileName: '', // Completed builds don't have a fileName displayed in Figma
        status: 'completed',
        isNew: job.seen === false, // Show "New" badge if not yet seen
        dateLabel: formatDate(job.completedAt),
    }));
    // Filter by status
    const draftsCount = draftPlans.length;
    const completedCount = completedBuildsList.length;
    // Get filtered list based on active tab
    const displayedPlans = activeTab === 'drafts' ? draftPlans : completedBuildsList;
    // Handle resume plan (for drafts)
    const handleResumePlan = (planId) => {
        console.log('[StartNewPlan] Resume plan:', planId);
        if (onResumePlan) {
            onResumePlan(planId);
        }
    };
    // Handle click on completed build - mark as seen and open detail view
    const handleViewCompletedBuild = (planId) => {
        console.log('[StartNewPlan] View completed build:', planId);
        markBuildAsSeen(planId);
        if (onViewBuildDetail) {
            onViewBuildDetail(planId);
        }
    };
    // Handle delete plan - shows confirmation dialog first
    const handleDeletePlan = async (planId) => {
        console.log('[StartNewPlan] handleDeletePlan called with planId:', planId);
        // Find the plan name for the confirmation message
        const plan = plans.find(p => p.id === planId);
        const planTitle = plan?.title || 'this plan';
        setConfirmDialog({
            isOpen: true,
            title: 'Delete Plan?',
            message: `Are you sure you want to delete "${planTitle}"? This action cannot be undone.`,
            confirmLabel: 'Delete',
            variant: 'danger',
            onConfirm: async () => {
                setConfirmDialog(null);
                try {
                    console.log('[StartNewPlan] Calling deletePlan from context...');
                    const success = await deletePlan(planId);
                    console.log('[StartNewPlan] deletePlan returned:', success);
                    if (success) {
                        console.log('[StartNewPlan] Plan deleted successfully:', planId);
                        // Reload plans to refresh the list
                        loadPlans();
                    }
                    else {
                        console.error('[StartNewPlan] Failed to delete plan:', planId);
                    }
                }
                catch (error) {
                    console.error('[StartNewPlan] Error deleting plan:', error);
                }
            },
        });
    };
    // Handle cancel build - shows confirmation dialog for queued/active builds
    const handleCancelBuild = async (planId) => {
        const job = inProgressJobs.find(j => j.planId === planId);
        if (!job) {
            // Job not found in inProgressJobs - might be completed or non-existent
            console.warn('[StartNewPlan] handleCancelBuild: Job not found in inProgressJobs:', planId);
            return;
        }
        const isQueued = job.status === 'queued' || job.status === 'initializing';
        const isPaused = job.status === 'paused';
        setConfirmDialog({
            isOpen: true,
            title: isQueued ? 'Remove from Queue?' : 'Cancel Build?',
            message: isQueued
                ? `Are you sure you want to remove "${job.planTitle}" from the queue?`
                : isPaused
                    ? `Are you sure you want to cancel "${job.planTitle}"? The partial build will be lost.`
                    : `Are you sure you want to cancel the current build of "${job.planTitle}"?`,
            confirmLabel: isQueued ? 'Remove' : 'Cancel Build',
            variant: 'danger',
            onConfirm: async () => {
                setConfirmDialog(null);
                try {
                    if (isQueued) {
                        await removeFromQueue(planId);
                    }
                    else {
                        await cancelBuild(planId);
                    }
                }
                catch (error) {
                    console.error('[StartNewPlan] Error cancelling/removing build:', error);
                }
            },
        });
    };
    // Close confirmation dialog
    const closeConfirmDialog = () => {
        setConfirmDialog(null);
    };
    return (_jsxs("div", { className: "flex px-[120px] pt-10 pb-20 flex-col items-start gap-6 flex-1 self-stretch rounded-xl border border-gray-300 bg-white shadow-[0_0_6px_-4px_rgba(0,0,0,0.10),0_4px_4px_0_rgba(0,0,0,0.06),0_0_2px_0_rgba(0,0,0,0.06),0_0_1px_0_rgba(0,0,0,0.04)]", children: [_jsx("h1", { className: "text-[15px] font-bold leading-[120%] text-gray-990", children: "Generate a deck from a presentation plan" }), _jsxs("div", { onClick: onStartNewPlan, className: "flex p-6 items-center gap-3 self-stretch rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors cursor-pointer shadow-sm", children: [_jsx("div", { className: "flex w-10 h-10 items-center justify-center rounded-full border border-purple-sparkle-soft bg-gray-100 shrink-0", children: _jsx(PlusIcon, { size: 16, variant: "gradient" }) }), _jsxs("div", { className: "flex flex-col gap-1 flex-1", children: [_jsx("h2", { className: "text-[13px] font-bold leading-[150%] text-gray-990", children: "Start a new plan" }), _jsx("p", { className: "text-[12px] text-gray-600 leading-[150%]", children: "Put together your deck plan and quickly generate a brand new deck." })] }), _jsx(ArrowIcon, { size: 16, color: "black", direction: "right" })] }), inProgressJobs.length > 0 && (_jsx(InProgressSection, { jobs: inProgressJobs, onJobSelect: onViewBuildDetail, onPause: pauseBuild, onResume: resumeBuild, onCancel: handleCancelBuild })), confirmDialog && (_jsx(ConfirmationDialog, { isOpen: confirmDialog.isOpen, title: confirmDialog.title, message: confirmDialog.message, confirmLabel: confirmDialog.confirmLabel, variant: confirmDialog.variant, onConfirm: confirmDialog.onConfirm, onCancel: closeConfirmDialog })), _jsxs("div", { className: "flex flex-col gap-4 self-stretch", children: [_jsxs("div", { className: "flex justify-between items-end self-stretch border-b border-gray-300", children: [_jsxs("div", { className: "flex items-center gap-0", children: [_jsxs("button", { onClick: () => setActiveTab('drafts'), className: `pb-3 px-3 text-[15px] font-bold leading-[150%] border-b-2 -mb-px transition-colors cursor-pointer ${activeTab === 'drafts'
                                            ? 'text-primary-purple-70 border-primary-purple-70'
                                            : 'text-gray-600 border-transparent hover:text-gray-500'}`, children: ["Drafts (", draftsCount, ")"] }), _jsxs("button", { onClick: () => setActiveTab('completed'), className: `pb-3 px-3 text-[15px] font-bold leading-[150%] border-b-2 -mb-px transition-colors cursor-pointer ${activeTab === 'completed'
                                            ? 'text-primary-purple-70 border-primary-purple-70'
                                            : 'text-gray-600 border-transparent hover:text-gray-500'}`, children: ["Completed (", completedCount, ")"] })] }), _jsxs("button", { className: "flex items-center gap-2 px-3 py-1.5 mb-2 rounded-lg cursor-pointer hover:opacity-60 transition-opacity", children: [_jsx("span", { className: "text-[13px] font-bold text-gray-990", children: "Sort" }), _jsx(SortIcon, { size: 16, color: "#111827", direction: "both" })] })] }), (isLoadingPlans || !isAuthReady) && (_jsx("div", { className: "flex items-center justify-center py-12", children: _jsxs("div", { className: "flex items-center gap-1 text-gray-500 text-[13px]", children: [!isAuthReady ? 'Connecting' : 'Loading plans', _jsx("lottie-player", { src: JSON.stringify(dotsAnimation), background: "transparent", speed: "1", style: { width: '24px', height: '24px' }, loop: true, autoplay: true })] }) })), plansError && !isLoadingPlans && isAuthReady && (_jsxs("div", { className: "flex flex-col items-center justify-center py-12 gap-2", children: [_jsx("span", { className: "text-red-500 text-[13px]", children: plansError }), _jsx("button", { onClick: () => loadPlans(), className: "text-primary-purple-70 text-[13px] hover:underline", children: "Try again" })] })), !isLoadingPlans && !plansError && isAuthReady && (_jsx(DraftsList, { activeTab: activeTab, drafts: displayedPlans, onResume: activeTab === 'drafts' ? handleResumePlan : handleViewCompletedBuild, onDelete: activeTab === 'drafts' ? handleDeletePlan : undefined }))] })] }));
};
export default StartNewPlan;
